import { Container } from 'unstated';
import { diff } from 'src/lib/time';
import alerts from 'src/lib/alerts';
import app from 'src/feathers';

const notifications = app.get('notifications');

class AppContainer extends Container {
  constructor(props) {
    super(props);
    this.state = {
      authenticated: false,
      user: {}
    };
  }
}
/*
  This app only needs one instance of appContainer.
  We do not really follow the unstated docs of passing
  in the class AppContainer and allow unstated to
  manage the instances. Instead we make ONE instance
  and share it everywhere (which unstated does too
  under the hood) But we want to get at our state from
  externally of a subscribe component.
*/
export const appContainer = new AppContainer();

// Throttle and listen to window resize
// See notes in below link about IE polyfill
// https://developer.mozilla.org/en-US/docs/Web/Events/resize
(function () {
  var throttle = function (type, name, obj) {
    obj = obj || window;
    var running = false;
    var func = function () {
      if (running) {
        return;
      }
      running = true;
      window.requestAnimationFrame(function () {
        obj.dispatchEvent(new window.CustomEvent(name));
        running = false;
      });
    };
    obj.addEventListener(type, func);
  };

  /* init - you can init any event */
  throttle('resize', 'optimizedResize');
})();

// set window width in state on change
window.addEventListener('optimizedResize', function (event) {
  appContainer.setState({ windowWidth: event.target.innerWidth });
});

export class WithRecordsContainer extends Container {
  constructor(props) {
    super(props);
    this.options = { ...props };
    this.options.service = app.service(this.options.service);
    const { defaultQuery, defaultPage } = this.options;

    this.state = {
      fetching: false,
      page: defaultPage,
      query: defaultQuery
    };

    // Because this component mainly handles inputs from
    // texts, dropdowns, etc, make the setState sync instead
    // of the default unstated async setState
    this.setState = (update, callback) => {
      this.state = { ...this.state, ...update };
      this._listeners.forEach((fn) => fn());
      if (callback) {
        callback({ ...this.state });
      }
    };
  }

  setQuery = (update, callback) => {
    return this.setState({ query: { ...update } }, callback);
  };

  search = () => {
    this.setQuery({ ...this.state.query, $page: undefined }, () =>
      this.goToPage()
    );
  };

  sort = async ({ name, value }) => {
    this.setQuery({ ...this.state.query, $sort: { [name]: value } }, () =>
      this.goToPage()
    );
  };

  goToPage = async (page = this.state.page, _query = this.state.query) => {
    const query = { ..._query };
    if (page && !isNaN(page)) {
      query.$page = page;
    }
    this.setState({ fetching: true }, (newState) => {
      return this.options.service
        .find({ query })
        .then(({ data: resources, total }) => {
          this.setState({
            ...this.state,
            fetching: false,
            page,
            query,
            total,
            resources
          });
          return resources;
        })
        .catch((error) => {
          app.get('sentryClient').captureException(error);
          const { redirect, message } = this.options;
          if (redirect) {
            this.setState({ redirect });
          }
          if (message) {
            alerts.errors(message);
          }
        });
    });
  };
}

const percentComplete = (stage, timePassed) => {
  const estimate = stage.adjusted_estimate || stage.original_estimate;
  return timePassed / estimate;
};

export class StageContainer extends Container {
  stageTimer = null;
  defaultState = {
    timePassed: 0,
    nearDueNotifSent: false,
    overdueNotifSent: false
  };
  state = { ...this.defaultState };

  setStageTimer = (stage) => {
    const interval = 1000;

    const timePassed = diff(new Date(), stage.start_time);

    const initialState = {
      timePassed,
      // If it is already due/overdue on initialization, do not send notifs
      nearDueNotifSent: percentComplete(stage, timePassed) >= 0.9,
      overdueNotifSent: percentComplete(stage, timePassed) >= 1
    };

    this.clearStageTimer(initialState);

    this.stageTimer = setInterval(() => {
      //  On every fifth render user diff
      // which will ensure this does not drift for more than 5
      // seconds because browsers handle setTimouts differently
      // when tab is not active or browser in the background, etc
      var timePassed;
      if (this.state.timePassed % 5 === 0) {
        timePassed = diff(new Date(), stage.start_time);
      } else {
        timePassed = this.state.timePassed + interval / 1000;
      }

      this.setState({ timePassed });

      // Send notifications
      this.sendNotifications(stage, this.state);
    }, interval);
    return this.stageTimer;
  };

  clearStageTimer = (nexState) => {
    this.setState(nexState || { ...this.defaultState });
    if (this.stageTimer) {
      clearInterval(this.stageTimer);
      this.stageTimer = null;
    }
  };

  sendNotifications = (stage, state) => {
    const percent = percentComplete(stage, state.timePassed);
    const { nearDueNotifSent, overdueNotifSent } = this.state;
    if (!nearDueNotifSent) {
      if (percent >= 0.9 && percent < 1) {
        notifications.notify('Stage timer almost up!', {
          body:
            'The stage is almost due to be completed. Move to the next stage when you are done.'
        });
        this.setState({ nearDueNotifSent: true });
      }
    }
    if (!overdueNotifSent) {
      if (percent >= 1) {
        notifications.notify('Stage past due!', {
          body:
            'The stage is past due. Be sure to update your progress if you have completed the stage.'
        });
        this.setState({ overdueNotifSent: true });
      }
    }
  };
}
